// services/adminAuthService.js
// Admin authentication and staff operations using the unified Axios Admin API client.
import adminApi, { handleAdminAuthFailure } from "./adminApi";
import { getErrorMessage } from "../utils/apiError";

export { handleAdminAuthFailure };

// ── token & profile helpers ───────────────────────────────────────────────────

export function getAdminToken() {
  return null;
}

export function getAdminRefreshToken() {
  return null;
}

export function clearAdminTokens() {
  localStorage.removeItem("jb_admin_token");
  localStorage.removeItem("jb_admin_refresh_token");
  localStorage.removeItem("jb_admin");
}

export function saveAdminProfile(admin) {
  if (admin) {
    localStorage.setItem("jb_admin", JSON.stringify(admin));
  } else {
    localStorage.removeItem("jb_admin");
  }
}

export function getStoredAdmin() {
  try {
    const raw = localStorage.getItem("jb_admin");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isAdminLoggedIn() {
  return !!getStoredAdmin();
}

export function invalidateAdminMeCache() {
  // Hook for cache invalidation
}

export async function refreshAdminAccessToken() {
  try {
    await adminApi.post("/api/admin/refresh");
    return { success: true };
  } catch (err) {
    console.warn("Failed to refresh admin token:", err);
    clearAdminTokens();
    return { success: false };
  }
}

// ── auth operations ───────────────────────────────────────────────────────────

/**
 * Log in as admin.
 * @param {string} username
 * @param {string} password
 * @param {string} captchaToken
 * @returns {{ success, admin, error }}
 */
export async function adminLogin(username, password, captchaToken) {
  try {
    const res = await adminApi.post("/api/admin/login", {
      username,
      password,
      captcha_token: captchaToken,
    });
    const admin = res.data?.admin;
    saveAdminProfile(admin);
    return { success: true, admin };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Login failed"),
    };
  }
}

/**
 * Fetch the logged-in admin's profile from server.
 * @returns {{ success, admin, error }}
 */
export async function fetchAdminMe() {
  try {
    const res = await adminApi.get("/api/admin/me");
    const admin = res.data?.admin;
    if (admin) {
      saveAdminProfile(admin);
      return { success: true, admin };
    }
    return { success: false, error: "Profile not found" };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Could not fetch admin profile"),
    };
  }
}

/**
 * Log out admin — clears session cookies and profile.
 */
export async function adminLogout() {
  try {
    await adminApi.post("/api/admin/logout");
  } catch {
    // Session cleanup regardless of network status
  }
  clearAdminTokens();
  invalidateAdminMeCache();
}

/**
 * Create a new staff account (super_admin only).
 * @param {{ name, username, password, role }} fields
 */
export async function createStaff(fields) {
  try {
    const res = await adminApi.post("/api/admin/create-staff", fields);
    return { success: true, admin: res.data?.admin };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Failed to create staff member"),
    };
  }
}

/**
 * List all admin accounts (super_admin only).
 */
export async function listAdmins() {
  try {
    const res = await adminApi.get("/api/admin/list");
    return { success: true, admins: res.data?.admins || [] };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Failed to fetch admin accounts"),
    };
  }
}

/**
 * Deactivate a staff account (super_admin only).
 * @param {number} adminId
 */
export async function deactivateAdmin(adminId) {
  try {
    await adminApi.patch(`/api/admin/${adminId}/deactivate`);
    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: getErrorMessage(err, "Failed to deactivate admin"),
    };
  }
}
